import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentOrgId } from '../auth';
import { useToast } from "@/hooks/use-toast";
import { ilikeOrValue, phoneIlikeOrValue } from "@/lib/postgrestOr";

/** Valores aceitos pelo enum company_size no banco (supabase/schema). */
const COMPANY_SIZE_ENUM = ['micro', 'small', 'medium', 'large', 'enterprise'] as const;
type CompanySizeEnum = typeof COMPANY_SIZE_ENUM[number];

/** Normaliza o valor de size para o enum do banco (evita conflito com rótulos em português). */
function normalizeCompanySize(size: string | null | undefined): CompanySizeEnum | null {
  if (!size || typeof size !== 'string') return null;
  const lower = size.trim().toLowerCase();
  const map: Record<string, CompanySizeEnum> = {
    micro: 'micro',
    pequena: 'small',
    small: 'small',
    média: 'medium',
    media: 'medium',
    medium: 'medium',
    grande: 'large',
    large: 'large',
    corporação: 'enterprise',
    corporacao: 'enterprise',
    enterprise: 'enterprise',
    startup: 'micro',
  };
  if (map[lower]) return map[lower];
  if (COMPANY_SIZE_ENUM.includes(lower as CompanySizeEnum)) return lower as CompanySizeEnum;
  return null;
}

/** Valores aceitos pelo enum company_status no banco (supabase/schema). */
const COMPANY_STATUS_ENUM = ['prospect', 'active', 'customer', 'churned'] as const;
type CompanyStatusEnum = typeof COMPANY_STATUS_ENUM[number];

/** Normaliza o valor de status para o enum do banco (evita conflito com rótulos em português). */
function normalizeCompanyStatus(status: string | null | undefined): CompanyStatusEnum | null {
  if (!status || typeof status !== 'string') return null;
  const lower = status.trim().toLowerCase();
  const map: Record<string, CompanyStatusEnum> = {
    prospect: 'prospect',
    ativa: 'active',
    active: 'active',
    ativo: 'active',
    cliente: 'customer',
    customer: 'customer',
    inativa: 'churned',
    inativo: 'churned',
    churned: 'churned',
  };
  if (map[lower]) return map[lower];
  if (COMPANY_STATUS_ENUM.includes(lower as CompanyStatusEnum)) return lower as CompanyStatusEnum;
  return null;
}

interface Company {
  id: string;
  name: string;
  industry: string | null;
  size: string | null;
  website: string | null;
  phone: string | null;
  email: string | null;
  location: string | null;
  description: string | null;
  status: string;
  domain: string | null;
  created_at: string;
  updated_at: string;
  total_leads: number | null;
  active_leads: number | null;
  converted_leads: number | null;
  value: number | null;
  last_contact_at?: string | null;
  custom_data?: Record<string, any>;
}

interface UseCompaniesParams {
  search?: string;
  industries?: string[];
  sizes?: string[];
  scores?: string[]; // fit_tier multi-select; '__none__' = sem dado (fit_tier IS NULL)
  lastContacts?: string[];
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  filterId?: string;
  page?: number;
  pageSize?: number;
}

function useCompanies(params?: UseCompaniesParams) {
  const { orgId } = useCurrentOrgId();

  return useQuery({
    queryKey: ['companies', orgId, params],
    queryFn: async () => {
      if (!orgId) throw new Error("Organization not found");

      const page = params?.page || 1;
      const pageSize = params?.pageSize || 20;

      // Ordenação: mapear sortBy da página para coluna do banco
      const sortColumn = params?.sortBy === 'lastContact' ? 'last_contact_at'
        : params?.sortBy === 'totalLeads' ? 'total_leads'
        : params?.sortBy === 'value' ? 'value'
        : params?.sortBy === 'name' ? 'name'
        : params?.sortBy === 'industry' ? 'industry'
        : params?.sortBy === 'size' ? 'size'
        : params?.sortBy === 'faturamento' ? 'faturamento'
        : 'created_at';
      const sortAscending = params?.sortOrder === 'asc';

      // Filtro salvo: server-side via RPC
      if (params?.filterId) {
        const { data, error } = await supabase.rpc('get_companies_page', {
          p_org_id: orgId,
          p_offset: (page - 1) * pageSize,
          p_limit: pageSize,
          p_search: params?.search?.trim() || null,
          p_industries: params?.industries?.length ? params.industries : null,
          p_sizes: params?.sizes?.length ? params.sizes : null,
          p_statuses: null,
          p_owner_ids: null,
          p_include_no_owner: false,
          p_sort_by: sortColumn,
          p_sort_order: sortAscending ? 'asc' : 'desc',
          p_filter_id: params.filterId,
        });
        if (error) throw error;
        return ((Array.isArray(data) ? data : []) as Company[]);
      }

      // Sem filtro salvo: paginação normal server-side com PostgREST
      let query = supabase
        .from('companies')
        .select('*, custom_data')
        .eq('org_id', orgId);

      if (params?.search?.trim()) {
        const term = ilikeOrValue(params.search.trim());
        const phoneTerm = phoneIlikeOrValue(params.search.trim());
        query = query.or(
          `name.ilike.${term},domain.ilike.${term},industry.ilike.${term},location.ilike.${term},email.ilike.${term},phone.ilike.${phoneTerm}`
        );
      }
      if (params?.industries?.length) {
        query = query.in('industry', params.industries);
      }
      if (params?.sizes?.length) {
        query = query.in('size', params.sizes as never[]);
      }
      if (params?.scores?.length) {
        const includeNone = params.scores.includes('__none__');
        const tiers = params.scores.filter((s) => s !== '__none__');
        if (includeNone && tiers.length) query = query.or(`fit_tier.in.(${tiers.join(',')}),fit_tier.is.null`);
        else if (includeNone) query = query.is('fit_tier', null);
        else query = query.in('fit_tier', tiers);
      }
      if (params?.lastContacts?.length) {
        const hasNoContact = params.lastContacts.includes('no_contact');
        const hasRecent = params.lastContacts.includes('recent');
        const hasOld = params.lastContacts.includes('old');
        if (hasNoContact && !hasRecent && !hasOld) {
          query = query.is('last_contact_at', null);
        } else if (hasRecent && !hasOld && !hasNoContact) {
          const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
          query = query.not('last_contact_at', 'is', null).gte('last_contact_at', sevenDaysAgo);
        } else if (hasOld && !hasRecent && !hasNoContact) {
          const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
          query = query.not('last_contact_at', 'is', null).lt('last_contact_at', sevenDaysAgo);
        }
      }

      query = query.order(sortColumn, { ascending: sortAscending, nullsFirst: false });
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to);

      const { data: rawData, error } = await query;
      if (error) throw error;
      return ((rawData || []) as Company[]);
    },
    enabled: !!orgId
  });
}

const COMPANIES_PAGE_SIZE = 50;

interface CompaniesPage {
  items: Company[];
  total: number;
  nextOffset: number | undefined;
}

type UseCompaniesInfiniteParams = Omit<UseCompaniesParams, 'page' | 'pageSize'>;

export function useCompaniesInfinite(params?: UseCompaniesInfiniteParams) {
  const { orgId } = useCurrentOrgId();

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isRefetching,
    isFetching,
  } = useInfiniteQuery<CompaniesPage, Error, { pages: CompaniesPage[] }, unknown[], number>({
    queryKey: ['companies-infinite', orgId, params],
    queryFn: async ({ pageParam }) => {
      if (!orgId) throw new Error("Organization not found");

      const from = pageParam ?? 0;
      const to = from + COMPANIES_PAGE_SIZE - 1;

      const sortColumn = params?.sortBy === 'lastContact' ? 'last_contact_at'
        : params?.sortBy === 'totalLeads' ? 'total_leads'
        : params?.sortBy === 'value' ? 'value'
        : params?.sortBy === 'name' ? 'name'
        : params?.sortBy === 'industry' ? 'industry'
        : params?.sortBy === 'size' ? 'size'
        : params?.sortBy === 'faturamento' ? 'faturamento'
        : 'created_at';
      const sortAscending = params?.sortOrder === 'asc';

      // Filtro salvo: server-side via RPC
      if (params?.filterId) {
        const [pageRes, countRes] = await Promise.all([
          supabase.rpc('get_companies_page', {
            p_org_id: orgId,
            p_offset: from,
            p_limit: COMPANIES_PAGE_SIZE,
            p_search: params?.search?.trim() || null,
            p_industries: params?.industries?.length ? params.industries : null,
            p_sizes: params?.sizes?.length ? params.sizes : null,
            p_statuses: null,
            p_owner_ids: null,
            p_include_no_owner: false,
            p_sort_by: sortColumn,
            p_sort_order: sortAscending ? 'asc' : 'desc',
            p_filter_id: params.filterId,
          }),
          from === 0
            ? supabase.rpc('get_companies_count', {
                p_org_id: orgId,
                p_search: params?.search?.trim() || null,
                p_industries: params?.industries?.length ? params.industries : null,
                p_sizes: params?.sizes?.length ? params.sizes : null,
                p_statuses: null,
                p_owner_ids: null,
                p_include_no_owner: false,
                p_filter_id: params.filterId,
              })
            : Promise.resolve({ data: null, error: null }),
        ]);
        if (pageRes.error) throw pageRes.error;
        const items = ((Array.isArray(pageRes.data) ? pageRes.data : []) as Company[]);
        const total = from === 0 ? Number(countRes.data ?? 0) : (data?.pages[0]?.total ?? items.length);
        return {
          items,
          total,
          nextOffset: items.length === COMPANIES_PAGE_SIZE ? from + COMPANIES_PAGE_SIZE : undefined,
        };
      }

      // Sem filtro salvo: paginação normal server-side
      let query = supabase
        .from('companies')
        .select('*, custom_data', { count: 'exact' })
        .eq('org_id', orgId);

      if (params?.search?.trim()) {
        const term = ilikeOrValue(params.search.trim());
        const phoneTerm = phoneIlikeOrValue(params.search.trim());
        query = query.or(
          `name.ilike.${term},domain.ilike.${term},industry.ilike.${term},location.ilike.${term},email.ilike.${term},phone.ilike.${phoneTerm}`
        );
      }
      if (params?.industries?.length) {
        query = query.in('industry', params.industries);
      }
      if (params?.sizes?.length) {
        query = query.in('size', params.sizes as never[]);
      }
      if (params?.scores?.length) {
        const includeNone = params.scores.includes('__none__');
        const tiers = params.scores.filter((s) => s !== '__none__');
        if (includeNone && tiers.length) query = query.or(`fit_tier.in.(${tiers.join(',')}),fit_tier.is.null`);
        else if (includeNone) query = query.is('fit_tier', null);
        else query = query.in('fit_tier', tiers);
      }
      if (params?.lastContacts?.length) {
        const hasNoContact = params.lastContacts.includes('no_contact');
        const hasRecent = params.lastContacts.includes('recent');
        const hasOld = params.lastContacts.includes('old');
        if (hasNoContact && !hasRecent && !hasOld) {
          query = query.is('last_contact_at', null);
        } else if (hasRecent && !hasOld && !hasNoContact) {
          const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
          query = query.not('last_contact_at', 'is', null).gte('last_contact_at', sevenDaysAgo);
        } else if (hasOld && !hasRecent && !hasNoContact) {
          const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
          query = query.not('last_contact_at', 'is', null).lt('last_contact_at', sevenDaysAgo);
        }
      }

      query = query.order(sortColumn, { ascending: sortAscending, nullsFirst: false });
      query = query.range(from, to);

      const { data: rawData, error: queryError, count } = await query;
      if (queryError) throw queryError;
      const items = (rawData || []) as Company[];
      return {
        items,
        total: count || 0,
        nextOffset: items.length === COMPANIES_PAGE_SIZE ? from + COMPANIES_PAGE_SIZE : undefined,
      };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset,
    enabled: !!orgId,
    staleTime: 2 * 60 * 1000,
  });

  const companies = useMemo(() => data?.pages.flatMap(p => p.items) || [], [data]);
  const totalCount = data?.pages[0]?.total || 0;

  return {
    companies,
    totalCount,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    isRefetching,
    isFetching,
  };
}

function getFieldValueForCompany(company: any, field: string): any {
  if (field.startsWith('custom_data.')) {
    const key = field.replace('custom_data.', '');
    return company.custom_data?.[key] ?? '';
  }
  const map: Record<string, any> = {
    name: company.name ?? '',
    industry: company.industry ?? '',
    size: company.size ?? '',
    website: company.website ?? '',
    phone: company.phone ?? '',
    email: company.email ?? '',
    location: company.location ?? '',
    total_leads: company.total_leads ?? 0,
    value: company.value ?? 0,
    last_contact_at: company.last_contact_at ?? '',
    created_at: company.created_at ?? '',
    updated_at: company.updated_at ?? '',
  };
  return map[field] ?? '';
}

function evaluateRuleForCompany(company: any, rule: any): boolean {
  const fieldValue = getFieldValueForCompany(company, rule.campo_filtrado);
  const ruleValue = rule.valor;
  if (!rule.campo_filtrado || !rule.operador) return false;
  switch (rule.operador) {
    case 'equals':
      return fieldValue === ruleValue;
    case 'not_equals':
      return fieldValue !== ruleValue;
    case 'contains':
      return String(fieldValue).toLowerCase().includes(String(ruleValue).toLowerCase());
    case 'not_contains':
      return !String(fieldValue).toLowerCase().includes(String(ruleValue).toLowerCase());
    case 'starts_with':
      return String(fieldValue).toLowerCase().startsWith(String(ruleValue).toLowerCase());
    case 'ends_with':
      return String(fieldValue).toLowerCase().endsWith(String(ruleValue).toLowerCase());
    case 'is_empty':
      return fieldValue == null || fieldValue === '';
    case 'is_not_empty':
      return fieldValue != null && fieldValue !== '';
    case 'greater_than':
      return Number(fieldValue) > Number(ruleValue);
    case 'less_than':
      return Number(fieldValue) < Number(ruleValue);
    case 'greater_than_or_equal':
      return Number(fieldValue) >= Number(ruleValue);
    case 'less_than_or_equal':
      return Number(fieldValue) <= Number(ruleValue);
    case 'in':
      return ruleValue.split(',').map((v: string) => v.trim()).includes(String(fieldValue));
    case 'not_in':
      return !ruleValue.split(',').map((v: string) => v.trim()).includes(String(fieldValue));
    default:
      return true;
  }
}

function applyFilterRulesForCompany(company: any, filter: any): boolean {
  const groups = filter.filtro_grupos_condicoes || [];
  let andResult = true;
  let orResult = false;
  let hasAndRules = false;
  let hasOrRules = false;
  for (const group of groups) {
    const rules = group.filtro_regras || [];
    if (group.tipo_agrupamento === 'AND' && rules.length > 0) {
      hasAndRules = true;
      andResult = rules.every((rule: any) => evaluateRuleForCompany(company, rule));
    } else if (group.tipo_agrupamento === 'OR' && rules.length > 0) {
      hasOrRules = true;
      orResult = rules.some((rule: any) => evaluateRuleForCompany(company, rule));
    }
  }
  if (hasAndRules && hasOrRules) return andResult && orResult;
  if (hasAndRules) return andResult;
  if (hasOrRules) return orResult;
  return true;
}

// Create company mutation
export function useCreateCompany() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { orgId } = useCurrentOrgId();

  return useMutation({
    mutationFn: async (company: Partial<Company>) => {
      const payload = { ...company, org_id: orgId };
      if (company.size !== undefined) {
        payload.size = normalizeCompanySize(company.size) ?? undefined;
      }
      if (company.status !== undefined) {
        payload.status = normalizeCompanyStatus(company.status) ?? undefined;
      }
      const { data, error } = await supabase
        .from('companies')
        .insert([payload] as never)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      toast({
        title: "Empresa criada",
        description: "A empresa foi criada com sucesso.",
      });
    },
    onError: (error: any) => {
      console.error('Error creating company:', error);
      toast({
        title: "Erro ao criar empresa",
        description: error.message || "Não foi possível criar a empresa.",
        variant: "destructive",
      });
    },
  });
}

// Update company mutation
export function useUpdateCompany() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...company }: Partial<Company> & { id: string }) => {
      const payload = { ...company };
      if (company.size !== undefined) {
        payload.size = normalizeCompanySize(company.size) ?? undefined;
      }
      if (company.status !== undefined) {
        payload.status = normalizeCompanyStatus(company.status) ?? undefined;
      }
      const { data, error } = await supabase
        .from('companies')
        .update(payload as never)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      toast({
        title: "Empresa atualizada",
        description: "A empresa foi atualizada com sucesso.",
      });
    },
    onError: (error: any) => {
      console.error('Error updating company:', error);
      toast({
        title: "Erro ao atualizar empresa",
        description: error.message || "Não foi possível atualizar a empresa.",
        variant: "destructive",
      });
    },
  });
}

// Delete company mutation
function useDeleteCompany() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (companyIds: string[]) => {
      const { error } = await supabase
        .from('companies')
        .delete()
        .in('id', companyIds);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      toast({
        title: "Empresa(s) excluída(s)",
        description: "A(s) empresa(s) foi(ram) excluída(s) com sucesso.",
      });
    },
    onError: (error: any) => {
      console.error('Error deleting company:', error);
      toast({
        title: "Erro ao excluir empresa(s)",
        description: error.message || "Não foi possível excluir a(s) empresa(s).",
        variant: "destructive",
      });
    },
  });
}